import { useCallback, useMemo, useRef, type FormEvent, type ReactNode, type RefObject } from 'react'
import { useIntl } from 'react-intl'

import { Skeleton } from '@/shared/layout/Skeleton'
import { Button, cn, Dialog, type DialogSize } from '@/shared/ui'
import { focusRing } from '@/shared/ui/cn'
import { Check } from '@/shared/ui/icons'
import { useLoadMoreFocus, type FocusRef } from '@/shared/ui/use-list-focus'

/** A record a chooser lists: its id, its name, and an optional detail beside it. */
export interface ChooserItem {
  readonly id: string
  readonly label: string
  readonly sublabel?: string
}

interface ChooserBase {
  readonly open: boolean
  readonly onClose: () => void
  readonly title: string
  readonly size?: DialogSize
  /** The search fields; pressing Enter in one searches. */
  readonly filters: ReactNode
  readonly onSearch: () => void
  /** `null` until a search runs. */
  readonly results: readonly ChooserItem[] | null
  readonly loading?: boolean
  /** The search failed; Search tries again. */
  readonly failed?: boolean
  /** The viewer may not see these records, which searching again cannot change. */
  readonly isForbidden?: boolean
  readonly hasMore?: boolean
  readonly onLoadMore?: () => void
  readonly onToggle: (item: ChooserItem) => void
  readonly onConfirm: () => void
}

export type ChooserDialogProps = ChooserBase &
  (
    | { readonly multi?: false; readonly selected: string | null }
    | { readonly multi: true; readonly selected: readonly string[] }
  )

/**
 * The mockups' chooser (`components/chooser/ChooserDialog.jsx`): search, pick one record, or
 * several with `multi`, then OK hands them back to the workflow that opened it. The caller owns
 * the search fields and the results; each result is a toggle button that says whether it is
 * picked.
 */
/** A field a person types or picks a search value in, such as the name box; never a hidden or disabled one. */
const searchFields = 'input:not([type=hidden]):not([disabled]):not([aria-hidden="true"]), textarea:not([disabled]), [role=combobox]:not([aria-disabled="true"])'

export function ChooserDialog(props: ChooserDialogProps) {
  const intl = useIntl()
  const { onSearch, onConfirm, onClose, onLoadMore } = props
  const picked = props.multi === true ? props.selected : props.selected === null ? [] : [props.selected]

  const search = useCallback(
    (event: FormEvent) => {
      event.preventDefault()
      // Its dialog is a portal, so the submit would otherwise reach a form the chooser opened from.
      event.stopPropagation()
      onSearch()
    },
    [onSearch],
  )

  // The first search field takes focus as the dialog opens, so typing searches at once; the
  // caller owns the fields, so it is found in the form.
  const formRef = useRef<HTMLFormElement>(null)
  const searchFieldRef = useMemo<RefObject<HTMLElement | null>>(
    () => ({
      get current() {
        return formRef.current?.querySelector<HTMLElement>(searchFields) ?? null
      },
    }),
    [],
  )

  const keys = useMemo(() => props.results?.map((item) => item.id) ?? [], [props.results])
  const moreFocus = useLoadMoreFocus(keys, props.loading === true)
  const { row: resultRef, fallback: emptyRef } = moreFocus
  const loadMore = useCallback(() => {
    moreFocus.loading()
    onLoadMore?.()
  }, [moreFocus, onLoadMore])

  return (
    <Dialog
      open={props.open}
      onClose={onClose}
      title={props.title}
      size={props.size ?? 'md'}
      initialFocusRef={searchFieldRef}
      actions={
        <>
          <Button variant="outlined" size="small" onClick={onClose}>
            {intl.formatMessage({ id: 'search.chooser.cancel' })}
          </Button>
          <Button size="small" onClick={onConfirm} disabled={picked.length === 0}>
            {intl.formatMessage({ id: 'search.chooser.ok' })}
          </Button>
        </>
      }
    >
      <form ref={formRef} onSubmit={search} className="flex flex-col gap-4">
        {props.filters}
        <div>
          <Button type="submit" size="small">
            {intl.formatMessage({ id: 'search.chooser.search' })}
          </Button>
        </div>
      </form>

      {/* Always present, so a screen reader hears a search start and what it found. */}
      <output className="sr-only">
        {props.loading === true
          ? intl.formatMessage({ id: 'search.results.loading' })
          : props.isForbidden === true
            ? intl.formatMessage({ id: 'search.results.noAccess' })
            : props.results === null
            ? ''
            : intl.formatMessage({ id: 'search.chooser.found' }, { count: props.results.length })}
      </output>

      {props.loading === true && props.results === null && (
        <div className="flex flex-col gap-1 border-t border-divider pt-3">
          <Skeleton className="h-9 w-full" />
          <Skeleton className="h-9 w-full" />
        </div>
      )}

      {props.isForbidden === true && (
        <p className="border-t border-divider pt-3 text-sm text-text-secondary">{intl.formatMessage({ id: 'search.results.noAccess' })}</p>
      )}

      {props.failed === true && (
        <p role="alert" className="text-sm text-error-text">
          {intl.formatMessage({ id: 'search.chooser.error' })}
        </p>
      )}

      {props.results !== null && (
        <div className="flex flex-col gap-1 border-t border-divider pt-3">
          {props.results.length === 0 && (
            <p ref={emptyRef} className="text-sm text-text-secondary">
              {intl.formatMessage({ id: 'search.chooser.empty' })}
            </p>
          )}
          {props.results.map((item) => (
            <ChooserRow
              key={item.id}
              item={item}
              multi={props.multi === true}
              picked={picked.includes(item.id)}
              onToggle={props.onToggle}
              buttonRef={resultRef(item.id)}
            />
          ))}
          {props.hasMore === true && onLoadMore !== undefined && (
            <div className="pt-2">
              <Button variant="outlined" size="small" onClick={loadMore} busy={props.loading === true}>
                {intl.formatMessage({ id: 'search.results.loadMore' })}
              </Button>
            </div>
          )}
        </div>
      )}
    </Dialog>
  )
}

function ChooserRow(props: {
  readonly item: ChooserItem
  readonly multi: boolean
  readonly picked: boolean
  readonly onToggle: (item: ChooserItem) => void
  readonly buttonRef: FocusRef
}) {
  const intl = useIntl()
  const { item, onToggle, buttonRef } = props
  const toggle = useCallback(() => onToggle(item), [item, onToggle])

  return (
    <button
      ref={buttonRef}
      type="button"
      onClick={toggle}
      aria-pressed={props.picked}
      className={cn(
        'flex cursor-pointer items-center justify-between gap-3 rounded px-3 py-2 text-left text-sm transition',
        props.picked
          ? 'bg-primary-soft font-medium text-primary-main'
          : 'text-text-primary hover:bg-background-contrast-sm',
        focusRing,
      )}
    >
      <span>
        {item.label}
        {item.sublabel !== undefined && (
          <>
            {/* Read as a pause between the name and its details, which the eye sees apart. */}
            <span className="sr-only">{intl.formatMessage({ id: 'search.chooser.detailsSeparator' })}</span>
            <span className="ml-2 font-normal text-text-secondary">{item.sublabel}</span>
          </>
        )}
      </span>
      {props.multi && (
        <span
          aria-hidden="true"
          className={cn(
            'flex h-4 w-4 shrink-0 items-center justify-center rounded-xs border transition',
            props.picked
              ? 'border-primary-main bg-primary-main text-primary-contrast'
              : 'border-text-light bg-background',
          )}
        >
          {props.picked && <Check size={12} />}
        </span>
      )}
    </button>
  )
}
